import React from 'react';
import { TouchableOpacity, ActivityIndicator } from 'react-native';
import { AuthenticatedImage } from '@/components';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import {
  Calendar,
  MapPin,
  BadgeCheck,
  Star,
  Bell,
  MessageCircle,
  X,
  Check,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { COLORS } from '@/constants/colors';
import { useMeetingAnnouncement } from '@/api/hooks/useMeetingAnnouncements';
import { usePublicProfile } from '@/api/hooks/useProfile';
import type { MeetingAnnouncementApplicationView } from '@/api/types';
import {
  getStatusFromCode,
  formatDate,
  formatTime,
  formatTimeAgo,
} from './utils';

type ApplicationCardProps = {
  application: MeetingAnnouncementApplicationView;
  cardBg: string;
  textColor: string;
  subtitleColor: string;
  borderColor: string;
  onReminderPress?: () => void;
  onWritePress?: () => void;
  onCancelPress?: () => void;
};

export const ApplicationCard: React.FC<ApplicationCardProps> = ({
  application,
  cardBg,
  textColor,
  subtitleColor,
  borderColor,
  onReminderPress,
  onWritePress,
  onCancelPress,
}) => {
  const { t } = useTranslation();
  const themeName = useThemeName();
  const isDark = themeName === 'dark';

  // Fetch announcement details
  const { data: announcement, isLoading: isLoadingAnnouncement } =
    useMeetingAnnouncement(application.meetingAnnouncementId);

  // Fetch host profile for photo
  const { data: hostProfile } = usePublicProfile(
    announcement?.createdById || '',
  );

  const status = getStatusFromCode(application.statusCode);
  const hostPhoto =
    hostProfile?.photos?.find(p => p.isDefault)?.thumbnailUrl ||
    hostProfile?.photos?.[0]?.thumbnailUrl;
  const hostName =
    announcement?.createdByNickname || hostProfile?.nickName || 'Anonim';
  const hostAge = hostProfile?.age;
  const isVerified = hostProfile?.isVerified;
  const rating = hostProfile?.ratingAverage;
  const ratingCount = hostProfile?.ratingCount;

  // Status badge config
  const getStatusBadge = () => {
    switch (status) {
      case 'accepted':
        return {
          label: t('screens.myApplications.statusAccepted', 'Zaakceptowane'),
          bgColor: 'rgba(34, 197, 94, 0.15)',
          textColor: '#22c55e',
        };
      case 'rejected':
        return {
          label: t('screens.myApplications.statusRejected', 'Odrzucone'),
          bgColor: 'rgba(239, 68, 68, 0.15)',
          textColor: '#ef4444',
        };
      default:
        return {
          label: t('screens.myApplications.statusPending', 'Oczekuje'),
          bgColor: 'rgba(251, 191, 36, 0.15)',
          textColor: '#f59e0b',
        };
    }
  };

  const statusBadge = getStatusBadge();

  if (isLoadingAnnouncement) {
    return (
      <YStack
        backgroundColor={cardBg}
        borderRadius={16}
        padding="$4"
        borderWidth={1}
        borderColor={borderColor}
        alignItems="center"
        justifyContent="center"
        height={200}
      >
        <ActivityIndicator size="small" color={COLORS.PRIMARY} />
      </YStack>
    );
  }

  return (
    <YStack
      backgroundColor={cardBg}
      borderRadius={16}
      padding="$4"
      borderWidth={1}
      borderColor={borderColor}
      gap="$3"
    >
      {/* Header: Host photo, name, age, verification + Status badge */}
      <XStack gap="$3" alignItems="flex-start">
        {/* Host Photo */}
        <YStack
          width={50}
          height={50}
          borderRadius={25}
          overflow="hidden"
          backgroundColor={subtitleColor}
        >
          {hostPhoto ? (
            <AuthenticatedImage
              uri={hostPhoto}
              style={{ width: 50, height: 50 }}
            />
          ) : (
            <YStack flex={1} alignItems="center" justifyContent="center">
              <Text fontSize={20} color="#fff">
                {hostName.charAt(0).toUpperCase()}
              </Text>
            </YStack>
          )}
        </YStack>

        {/* Host Info */}
        <YStack flex={1}>
          <XStack alignItems="center" gap="$1">
            <Text
              fontSize={16}
              fontWeight="600"
              fontFamily="Poppins"
              color={textColor}
            >
              {hostName}
            </Text>
            {isVerified && <BadgeCheck size={16} color="#22c55e" />}
          </XStack>

          {hostAge && (
            <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
              {hostAge} {t('screens.myApplications.years', 'lata')}
            </Text>
          )}

          {rating !== undefined && (
            <XStack alignItems="center" gap="$1">
              <Star size={12} color="#fbbf24" fill="#fbbf24" />
              <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
                {rating > 0
                  ? `${rating.toFixed(1)} (${ratingCount} ${t(
                      'screens.myApplications.reviews',
                      'opinii',
                    )})`
                  : t('screens.myApplications.noRating', 'Brak oceny')}
              </Text>
            </XStack>
          )}
        </YStack>

        {/* Status Badge */}
        <XStack
          backgroundColor={statusBadge.bgColor}
          paddingHorizontal="$3"
          paddingVertical="$1.5"
          borderRadius={8}
        >
          <Text
            fontSize={12}
            fontWeight="600"
            fontFamily="Poppins"
            color={statusBadge.textColor}
          >
            {statusBadge.label}
          </Text>
        </XStack>
      </XStack>

      {/* Meeting Title */}
      <Text
        fontSize={16}
        fontWeight="600"
        fontFamily="Poppins"
        color={textColor}
      >
        {announcement?.additionalInfo ||
          t('screens.myApplications.meeting', 'Spotkanie')}
      </Text>

      {/* Meeting Details: Date + Location */}
      <XStack gap="$4" flexWrap="wrap">
        <XStack alignItems="center" gap="$1">
          <Calendar size={14} color={subtitleColor} />
          <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
            {announcement?.meetingDate
              ? formatDate(announcement.meetingDate)
              : ''}
            ,{' '}
            {announcement?.meetingTime
              ? formatTime(announcement.meetingTime)
              : ''}
          </Text>
        </XStack>
        <XStack alignItems="center" gap="$1" flex={1}>
          <MapPin size={14} color={subtitleColor} />
          <Text
            fontSize={12}
            fontFamily="Poppins"
            color={subtitleColor}
            numberOfLines={1}
          >
            {announcement?.meetingAddress || ''}
          </Text>
        </XStack>
      </XStack>

      {/* Motivation in quotes */}
      <YStack
        backgroundColor={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
        padding="$3"
        borderRadius={12}
      >
        <Text
          fontSize={13}
          fontFamily="Poppins"
          color={subtitleColor}
          fontStyle="italic"
        >
          "{application.motivation}"
        </Text>

        {/* Price info */}
        {(application.proposedAmount || announcement?.paymentAmount) && (
          <XStack marginTop="$2" alignItems="center" gap="$1">
            <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
              {t('screens.myApplications.price', 'Cena')}:
            </Text>
            {application.proposedAmount &&
            announcement?.paymentAmount &&
            application.proposedAmount !== announcement.paymentAmount ? (
              <>
                <Text
                  fontSize={12}
                  fontFamily="Poppins"
                  color={subtitleColor}
                  textDecorationLine="line-through"
                >
                  {announcement.paymentAmount} zł
                </Text>
                <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
                  →
                </Text>
                <Text
                  fontSize={12}
                  fontWeight="600"
                  fontFamily="Poppins"
                  color={textColor}
                >
                  {application.proposedAmount} zł
                </Text>
                {status === 'accepted' && (
                  <Text
                    fontSize={11}
                    fontFamily="Poppins"
                    color="#22c55e"
                    marginLeft="$1"
                  >
                    (
                    {t('screens.myApplications.priceAccepted', 'zaakceptowana')}
                    )
                  </Text>
                )}
              </>
            ) : (
              <Text
                fontSize={12}
                fontWeight="600"
                fontFamily="Poppins"
                color={textColor}
              >
                {application.proposedAmount || announcement?.paymentAmount} zł
              </Text>
            )}
          </XStack>
        )}
      </YStack>

      {/* Time ago */}
      <Text fontSize={11} fontFamily="Poppins" color={subtitleColor}>
        {status === 'pending'
          ? `${t('screens.myApplications.sentAgo', 'Wysłane')} ${formatTimeAgo(
              application.createdAt,
              t,
            )}`
          : status === 'rejected'
          ? `${t(
              'screens.myApplications.rejectedAgo',
              'Odrzucone',
            )} ${formatTimeAgo(application.updatedAt, t)}`
          : `${t(
              'screens.myApplications.acceptedAgo',
              'Zaakceptowane',
            )} ${formatTimeAgo(application.updatedAt, t)}`}
      </Text>

      {/* Reminder settings section for accepted */}
      {status === 'accepted' && (
        <YStack
          backgroundColor={
            isDark ? 'rgba(94, 128, 156, 0.1)' : 'rgba(94, 128, 156, 0.05)'
          }
          padding="$4"
          borderRadius={12}
          gap="$3"
        >
          <Text
            fontSize={13}
            fontWeight="500"
            fontFamily="Poppins"
            color={textColor}
          >
            {t(
              'screens.myApplications.reminderSettings',
              'Ustaw przypomnienia',
            )}
            :
          </Text>
          <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
            24h {t('screens.myApplications.beforeMeeting', 'przed spotkaniem')}
          </Text>
          <XStack alignItems="center" gap="$2">
            <Check size={16} color="#22c55e" />
            <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
              2h {t('screens.myApplications.beforeMeeting', 'przed spotkaniem')}
            </Text>
          </XStack>
          <XStack alignItems="center" gap="$2">
            <Check size={16} color="#22c55e" />
            <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
              30 min{' '}
              {t('screens.myApplications.beforeMeeting', 'przed spotkaniem')}
            </Text>
          </XStack>
        </YStack>
      )}

      {/* Rejection reason for rejected */}
      {status === 'rejected' && (
        <YStack
          backgroundColor={
            isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)'
          }
          padding="$3"
          borderRadius={12}
          borderWidth={1}
          borderColor="rgba(239, 68, 68, 0.2)"
        >
          <Text
            fontSize={12}
            fontWeight="500"
            fontFamily="Poppins"
            color="#ef4444"
          >
            {t('screens.myApplications.rejectionReason', 'Powód odrzucenia')}:
          </Text>
          <Text
            fontSize={12}
            fontFamily="Poppins"
            color={subtitleColor}
            marginTop="$1"
          >
            {t(
              'screens.myApplications.rejectionReasonDefault',
              'Dziękuję za aplikację, ale wybrałam inną osobę. Powodzenia!',
            )}
          </Text>
        </YStack>
      )}

      {/* Action Buttons */}
      <XStack gap="$3" marginTop="$1">
        {status === 'accepted' && (
          <>
            <TouchableOpacity style={{ flex: 1 }} onPress={onReminderPress}>
              <YStack
                backgroundColor="transparent"
                borderRadius={12}
                paddingVertical="$2.5"
                alignItems="center"
                borderWidth={1}
                borderColor={COLORS.PRIMARY}
              >
                <Text fontSize={14} fontFamily="Poppins" color={COLORS.PRIMARY}>
                  {t('screens.myApplications.reminder', 'Przypomnienie')}
                </Text>
              </YStack>
            </TouchableOpacity>

            <TouchableOpacity style={{ flex: 1 }} onPress={onWritePress}>
              <YStack
                backgroundColor={COLORS.PRIMARY}
                borderRadius={12}
                paddingVertical="$2.5"
                alignItems="center"
              >
                <Text fontSize={14} fontFamily="Poppins" color="white">
                  {t('screens.myApplications.write', 'Napisz')}
                </Text>
              </YStack>
            </TouchableOpacity>
          </>
        )}

        {status === 'pending' && (
          <TouchableOpacity style={{ flex: 1 }} onPress={onCancelPress}>
            <YStack
              backgroundColor="rgba(239, 68, 68, 0.1)"
              borderRadius={12}
              paddingVertical="$2.5"
              alignItems="center"
            >
              <Text fontSize={14} fontFamily="Poppins" color="#ef4444">
                {t('screens.myApplications.cancel', 'Anuluj')}
              </Text>
            </YStack>
          </TouchableOpacity>
        )}
      </XStack>
    </YStack>
  );
};
